/** Назва типу запису для підстановки `{name}` у підписи меню й заголовки списку («Усі {name}»). */
import type { Lang } from '@platform/core/types';
import { isPluralName } from './pluralName';

/** Загальна назва записів, коли назва типу не узгоджується з «Усі» (однина) або тип названо абревіатурою модуля; у меню модулів без власного
 * меню українською — завжди («Усі записи», як у прототипі v4). */
export const RECORDS: Readonly<Record<Lang, string>> = { uk: 'записи', en: 'records' };

/** Назва-абревіатура (SWOT, KPI, OKR, CRM): незмінна, тож «Усі SWOT» читається; лише абревіатура модуля дає «записи». */
const ABBREVIATION = /^[A-ZА-ЯІЇЄҐ0-9]{2,}$/;

/**
 * Назва типу для `{name}` (чиста функція): назва в множині (isPluralName; `englishName` — англійська назва типу з метаданих для неоднозначних
 * українських закінчень) — з малої літери: «Усі ризики», «Усі завдання», «Усі пункти чек-листа»; назва в однині — загальне «записи» / "records":
 * «Усі записи» замість «Усі База знань», «Усі Факторний аналіз», «Усі Оплата помічників». Абревіатура лишається як є («Усі SWOT»), але тип, названий
 * абревіатурою модуля `moduleName` (OKR, CRM), — «записи». Англійські назви в метаданих пишуться з великої літери в кожному слові («Task Checklist
 * Items»), тож англійською мала літера — у кожному слові («All task checklist items»), українською — лише в першому («рішення Уряду»).
 */
export function menuTypeName(typeName: string, moduleName: string, lang: Lang, englishName?: string | null): string {
  if (ABBREVIATION.test(typeName)) return typeName === moduleName ? RECORDS[lang] : typeName;
  if (!isPluralName(typeName, englishName, lang)) return RECORDS[lang];
  return lang === 'en' ? typeName.split(' ').map((word) => lowerFirst(word, lang)).join(' ') : lowerFirst(typeName, lang);
}

/**
 * Перша літера — мала, якщо слово не абревіатура: друга літера (після апострофа, як у «З’їзди») має бути малою (SWOT, KPI, ШІ, MP, «Є-декларації»
 * лишаються).
 */
export function lowerFirst(text: string, lang: Lang): string {
  const first = text.charAt(0);
  const second = text.slice(1).replace(/^['’ʼ]/, '').charAt(0);
  const locale = lang === 'uk' ? 'uk-UA' : 'en-GB';
  const secondIsLower = second !== second.toLocaleUpperCase(locale) && second === second.toLocaleLowerCase(locale);
  return secondIsLower ? first.toLocaleLowerCase(locale) + text.slice(1) : text;
}

/** Слово з великих латинських літер (CRM, SWOT, KPI) — абревіатура, що лишається як є у правилі прототипу для англійського меню. */
const CAPS_WORD = /^[A-Z]{2,}$/;

/**
 * Назва типу для `{name}` в англійському меню за правилом прототипу v4 (`nm`: кожне слово з малої літери, крім абревіатур): «All document
 * center», «All factor analysis», «All CRM» — без розрізнення однини й множини. Чиста функція.
 */
export function englishMenuName(typeName: string): string {
  return typeName.split(' ').map((word) => (CAPS_WORD.test(word) ? word : word.toLowerCase())).join(' ');
}
